import axios from 'axios';
import { toast } from 'sonner';
import {
  FileText, UploadCloud, HardDrive, ExternalLink, Download, Trash2,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Files section (arquivos enviados) — extracted from ProjectDetails.js.
 * Pure view: receives all data + handlers from parent.
 */
const FilesSection = ({
  project, files, isAdmin, projectId, driveConnected,
  getAuthHeaders, onRefresh, onConnectDrive, confirm,
}) => {
  const handleDownload = async (file) => {
    try {
      const response = await axios.get(
        `${API}/projects/${projectId}/files/${file.id}/download`,
        { headers: getAuthHeaders(), responseType: 'arraybuffer' },
      );
      const blob = new Blob([response.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = file.file_name || 'arquivo';
      document.body.appendChild(a);
      a.click();
      setTimeout(() => { document.body.removeChild(a); URL.revokeObjectURL(url); }, 300);
    } catch {
      toast.error('Erro ao baixar arquivo');
    }
  };

  const handleDelete = async (file) => {
    const ok = await confirm('Excluir este arquivo?');
    if (!ok) return;
    try {
      await axios.delete(`${API}/projects/${projectId}/files/${file.id}`, { headers: getAuthHeaders() });
      toast.success('Arquivo removido!');
      onRefresh();
    } catch {
      toast.error('Erro ao remover arquivo');
    }
  };

  return (
    <div className="bg-slate-800/50 rounded-xl p-6 border border-slate-700">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-white flex items-center gap-2">
          <FileText className="h-5 w-5 text-teal-400" />
          Arquivos Enviados ({files.length})
        </h2>
        {project.drive_upload_folder_link && (
          <span className="flex items-center gap-1.5 text-xs bg-blue-500/10 text-blue-400 border border-blue-500/30 px-2.5 py-1 rounded-full" data-testid="drive-integration-badge">
            <UploadCloud className="h-3.5 w-3.5" />
            Drive Integrado
          </span>
        )}
      </div>

      {/* Drive connection notice for admins */}
      {isAdmin && project.drive_upload_folder_link && driveConnected === false && (
        <div className="bg-amber-500/5 border border-amber-500/20 rounded-lg px-4 py-3 mb-4 flex items-center justify-between" data-testid="drive-connect-notice">
          <p className="text-xs text-amber-300">
            <span className="font-medium">Conecte seu Google Drive</span> para habilitar o upload automático de arquivos para a pasta de destino do projeto.
          </p>
          <button
            onClick={onConnectDrive}
            className="ml-4 shrink-0 px-3 py-1.5 bg-amber-500/20 text-amber-300 rounded-lg text-xs font-medium hover:bg-amber-500/30 transition-colors"
            data-testid="connect-drive-btn"
          >
            Conectar Drive
          </button>
        </div>
      )}
      {isAdmin && project.drive_upload_folder_link && driveConnected === true && (
        <div className="bg-blue-500/5 border border-blue-500/20 rounded-lg px-4 py-2.5 mb-4 text-xs text-blue-300" data-testid="drive-share-notice">
          Drive conectado. A pasta de destino também deve ser compartilhada com <span className="font-mono bg-slate-700/50 px-1.5 py-0.5 rounded text-blue-200">dubleiros-drive@dubleiros-studios.iam.gserviceaccount.com</span> (Editor) como fallback.
        </div>
      )}

      {files.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {files.map(file => (
            <div key={file.id} className="bg-slate-700/50 rounded-lg p-3" data-testid={`file-${file.id}`}>
              <div className="flex items-center gap-3">
                {file.drive_file_id ? (
                  <UploadCloud className="h-5 w-5 text-blue-400 shrink-0" title="Salvo no Google Drive" />
                ) : (
                  <HardDrive className="h-5 w-5 text-slate-400 shrink-0" title="Armazenamento local" />
                )}
                <span className="text-white text-sm truncate flex-1">{file.file_name}</span>
                <div className="flex gap-1 shrink-0">
                  {file.drive_view_link && (
                    <a
                      href={file.drive_view_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 text-blue-400 hover:bg-blue-500/20 rounded transition-colors"
                      title="Abrir no Drive"
                      data-testid={`drive-link-file-${file.id}`}
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                  <button
                    onClick={() => handleDownload(file)}
                    className="p-1.5 text-teal-400 hover:bg-teal-500/20 rounded transition-colors"
                    title="Download"
                    data-testid={`download-file-${file.id}`}
                  >
                    <Download className="h-4 w-4" />
                  </button>
                  {isAdmin && (
                    <button
                      onClick={() => handleDelete(file)}
                      className="p-1.5 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                      title="Excluir"
                      data-testid={`delete-file-${file.id}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 mt-1.5 ml-8">
                {file.uploaded_by_name && (
                  <span className="text-xs text-slate-500">Enviado por: {file.uploaded_by_name}</span>
                )}
                {file.drive_file_id && (
                  <span className="text-xs bg-blue-500/10 text-blue-400 px-1.5 py-0.5 rounded">Drive</span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-slate-400 text-center py-6">Nenhum arquivo enviado ainda.</p>
      )}
    </div>
  );
};

export default FilesSection;
